import React, { useState, KeyboardEvent, forwardRef } from "react"
import { X } from "lucide-react"
import { Input } from "./input"
import { Label } from "./label"
import { cn } from "@/lib/utils"

interface EmailChipsInputProps {
  value?: string[]
  onChange?: (emails: string[]) => void
  placeholder?: string
  label?: string
  maxEmails?: number
  className?: string
  id?: string
}

const EmailChipsInput = forwardRef<HTMLInputElement, EmailChipsInputProps>(
  ({ value = [], onChange, placeholder, label, maxEmails = 5, className, id, ...props }, ref) => {
    const [inputValue, setInputValue] = useState("")
    const [error, setError] = useState("")

    const isValidEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email.trim())
    }

    const addEmail = (email: string) => {
      const trimmedEmail = email.trim()
      
      if (!trimmedEmail) return

      if (!isValidEmail(trimmedEmail)) {
        setError("Please enter a valid email address")
        return
      }

      if (value.includes(trimmedEmail)) {
        setError("This email has already been added")
        return
      }

      if (value.length >= maxEmails) {
        setError(`Maximum ${maxEmails} emails allowed`)
        return
      }

      setError("")
      onChange?.([...value, trimmedEmail])
      setInputValue("")
    }

    const removeEmail = (indexToRemove: number) => {
      const newEmails = value.filter((_, index) => index !== indexToRemove)
      onChange?.(newEmails)
      setError("")
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        addEmail(inputValue)
      } else if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
        removeEmail(value.length - 1)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      if (error) setError("")
    }

    const handleBlur = () => {
      if (inputValue.trim()) {
        addEmail(inputValue)
      }
    }

    return (
      <div className={cn("space-y-2", className)}>
        {label && <Label htmlFor={id}>{label}</Label>}
        
        <div className="relative">
          <div className="min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            {/* Email chips */}
            {value.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {value.map((email, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                  >
                    <span>{email}</span>
                    <button
                      type="button"
                      onClick={() => removeEmail(index)}
                      className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input field */}
            {value.length < maxEmails && (
              <div className="w-full">
                <input
                  ref={ref}
                  id={id}
                  type="email"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  placeholder={value.length === 0 ? placeholder : "Add another email..."}
                  className="w-full bg-transparent border-0 outline-none placeholder:text-muted-foreground text-sm placeholder:text-xs"
                  {...props}
                />
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <p className="text-xs text-red-500 mt-1">{error}</p>
          )}

          {/* Helper text */}
          <p className="text-xs text-muted-foreground mt-2">
            {value.length === 0 
              ? `Press Enter or Space to add email. Max ${maxEmails} emails.`
              : `${value.length}/${maxEmails} emails added. Press Enter or Space to add more.`
            }
          </p>
        </div>
      </div>
    )
  }
)

EmailChipsInput.displayName = "EmailChipsInput"

export { EmailChipsInput } 